import type { Team } from "@repo/shared/types";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ValuePickerDialog } from "@/components/ui/value-picker-dialog";
import { ValuePickerTrigger } from "@/components/ui/value-picker-trigger";
import { apiErrorCode, apiErrorMessage } from "@/lib/api/_client";
import { useActiveTenantUsers } from "@/lib/hooks/use-clients";
import { useCreateTeam, useUpdateTeam } from "@/lib/hooks/use-teams";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./team-dialog.i18n";

type TeamDialogProps = {
  open: boolean;
  team: Team | null;
  onClose: () => void;
};

export function TeamDialog({ open, team, onClose }: TeamDialogProps) {
  const t = useT(T);
  const { tenantUsers } = useActiveTenantUsers();
  const create = useCreateTeam();
  const update = useUpdateTeam();
  const [name, setName] = useState("");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(team?.name ?? "");
    setMemberIds(team?.memberIds ?? []);
    setError(null);
    setPickerOpen(false);
  }, [team, open]);

  const options = tenantUsers.map((u) => ({ value: u.id, label: u.name }));
  const selectedNames = memberIds
    .map((id) => tenantUsers.find((u) => u.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  const isPending = create.isPending || update.isPending;

  const triggerValue =
    memberIds.length === 0
      ? null
      : selectedNames.length === memberIds.length && selectedNames.length <= 3
        ? selectedNames.join(", ")
        : t.membersSelected(String(memberIds.length));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const body = { name: name.trim(), memberIds };
    try {
      if (team) {
        await update.mutateAsync({ id: team.id, body });
      } else {
        await create.mutateAsync(body);
      }
      onClose();
      toast.success(team ? t.saved : t.added);
    } catch (err) {
      setError(
        apiErrorCode(err) === "conflict" ? t.duplicate : apiErrorMessage(err, t.failedToSave),
      );
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogScrollContent className="max-w-lg">
          <DialogScrollHeader>
            <DialogTitle>{team ? t.editTeam : t.addTeam}</DialogTitle>
            <DialogDescription>{team ? t.editDescription : t.addDescription}</DialogDescription>
          </DialogScrollHeader>

          <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
            <DialogBody className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="team-name">
                  {t.name} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="team-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.namePlaceholder}
                  maxLength={100}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="team-members">{t.members}</Label>
                <ValuePickerTrigger
                  id="team-members"
                  value={triggerValue}
                  placeholder={t.membersPlaceholder}
                  onOpen={() => setPickerOpen(true)}
                  onClear={() => setMemberIds([])}
                />
              </div>
            </DialogBody>

            <DialogScrollFooter>
              <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" loading={isPending} disabled={name.trim() === ""}>
                {team ? t.save : t.addTeam}
              </Button>
            </DialogScrollFooter>
          </form>
        </DialogScrollContent>
      </Dialog>

      <ValuePickerDialog
        open={open && pickerOpen}
        options={options}
        selectedValues={memberIds}
        labels={{
          title: t.pickerTitle,
          description: (count) => t.membersSelected(String(count)),
          search: t.search,
          noOptionsFound: t.noOptionsFound,
        }}
        onClose={() => setPickerOpen(false)}
        onSave={(values) => {
          setMemberIds(values);
          setPickerOpen(false);
        }}
      />
    </>
  );
}
